import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { SelectDropdown, TextInput } from "../../reusables/FormElements";

import { IEmployerData, IEmployerFormProps, EmployerField } from "../../../interfaces/forms";
import { ISelectDropdownOption } from "../../../interfaces/form_elements";

import { OPBooleanOptions, OPEmployerTypes, OPLMIAInPastOptions, OPNAICS } from "../../../data/options";

import { closeModal, openModal } from "../../../store/slices/modal";

import useSaveEmployer from "../../../hooks/employers/useSaveEmployer";
import { TEmployerType } from "../../../interfaces/types";

const EmployerForm = (props: IEmployerFormProps) => {
    const { employerData, selected, setSelected, setExpanded } = props; // eslint-disable-line

    const dispatch = useDispatch();

    const requiredFields: EmployerField[] = ["legal_name", "operating_name", "type", "naics", "cra_number", "tech_company", "non_canadian_emp", "lmia_in_past"];
    const selectFields: EmployerField[] = ["type", "naics", "tech_company", "lmia_in_past"];

    const { mutate: saveEmployer } = useSaveEmployer();

    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<IEmployerData>({
        id: null,
        organization_id: null,
        type: { value: { value: "", label: "" }, error: "" },
        operating_name: { value: "", error: "" },
        legal_name: { value: "", error: "" },
        naics: { value: { value: "", label: "" }, error: "" },
        cra_number: { value: "", error: "" },
        tech_company: { value: { value: "", label: "" }, error: "" },
        non_canadian_emp: { value: "", error: "" },
        lmia_in_past: { value: { value: "", label: "" }, error: "" },
    });

    useEffect(() => {
        if (employerData) {
            const type = OPEmployerTypes.find(type => type.value === employerData.type);
            const naics = OPNAICS.find(naics => naics.value === employerData.naics);
            const techCompany = OPBooleanOptions.find(option => option.value === (employerData.tech_company ? 'true' : 'false'));
            const lmiaInPast = OPLMIAInPastOptions.find(option => option.value === (employerData.lmia_in_past === null ? 'null' : employerData.lmia_in_past === false ? 'false' : 'true'));

            setFormData({
                id: employerData.id > 0 ? employerData.id : null,
                organization_id: employerData.organization_id,
                legal_name: { value: employerData.legal_name ?? '', error: '' },
                operating_name: { value: employerData.operating_name ?? '', error: '' },
                type: { value: { value: type?.value ?? '', label: type?.label ?? '' }, error: '' },
                naics: { value: { value: naics?.value ?? '', label: naics?.label ?? '' }, error: '' },
                cra_number: { value: employerData.cra_number ?? '', error: '' },
                tech_company: { value: { value: techCompany?.value ?? '', label: techCompany?.label ?? '' }, error: '' },
                non_canadian_emp: { value: employerData.non_canadian_emp.toString() ?? '', error: '' },
                lmia_in_past: { value: { value: lmiaInPast?.value ?? '', label: lmiaInPast?.label ?? '' }, error: '' },
            });
        } else {
            setFormData({
                id: null,
                organization_id: null,
                type: { value: { value: "", label: "" }, error: "" },
                operating_name: { value: "", error: "" },
                legal_name: { value: "", error: "" },
                naics: { value: { value: "", label: "" }, error: "" },
                cra_number: { value: "", error: "" },
                tech_company: { value: { value: "", label: "" }, error: "" },
                non_canadian_emp: { value: "", error: "" },
                lmia_in_past: { value: { value: "", label: "" }, error: "" },
            });
        };

        // eslint-disable-next-line
    }, [employerData]);

    const onChangeText = (name: EmployerField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onChangeSelect = (name: EmployerField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.type.value.value === (employerData?.type ?? '') &&
            formData.operating_name.value === (employerData?.operating_name ?? '') &&
            formData.legal_name.value === (employerData?.legal_name ?? '') &&
            formData.naics.value.value === (employerData?.naics ?? '') &&
            formData.cra_number.value === (employerData?.cra_number ?? '') &&
            formData.tech_company.value.value === (employerData?.tech_company ? 'true' : 'false') &&
            formData.non_canadian_emp.value === (employerData?.non_canadian_emp?.toString() ?? '') &&
            formData.lmia_in_past.value.value === (employerData?.lmia_in_past === null ? 'null' : employerData?.lmia_in_past === false ? 'false' : 'true');

        return emptyFields || isDataSame || isProcessing;
    };

    const onSave = () => {
        if (isDisabled() || !selected || !formData.organization_id) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id" || key === "organization_id") return;
                newData[key as EmployerField].error = "";
            });
            return newData;
        });

        setIsProcessing(true);

        saveEmployer({
            payload: {
                id: formData.id,
                organization_id: formData.organization_id,
                type: formData.type.value.value as TEmployerType,
                operating_name: formData.operating_name.value,
                legal_name: formData.legal_name.value,
                naics: formData.naics.value.value,
                cra_number: formData.cra_number.value,
                tech_company: formData.tech_company.value.value === 'true',
                non_canadian_emp: formData.non_canadian_emp.value?.trim() ? parseInt(formData.non_canadian_emp.value) : 0,
                lmia_in_past: formData.lmia_in_past.value.value === 'null' ? null : formData.lmia_in_past.value.value === 'true' ? true : false,
            },
            setIsProcessing,
            selected,
            setSelected,
        });
    };

    const onDelete = () => {
        if (!selected) return;

        if (!formData.id) {
            setSelected(null);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Confirm delete employer?",
                content: "This will also delete all associated items. This action cannot be undone.",
                actionTxt: "Delete",
                action: () => {
                    // setIsProcessing(true);

                    // deleteEmployer({
                    //     id: formData.id as number,
                    //     setIsProcessing,
                    //     setSelected,
                    //     setExpanded,
                    // });

                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    return (
        <div className="immployer_admin__common_form">
            <div className="immployer_admin__common_form__inputs">
                <TextInput
                    name="cra_number"
                    type="text"
                    max_length={9}
                    pattern_mask="integer"
                    label="CRA Number"
                    required={true}
                    errorMsg={formData.cra_number.error}
                    placeholder="CRA Number"
                    onChange={(value: string) => onChangeText("cra_number", value)}
                    value={formData.cra_number.value}
                />
                <TextInput
                    name="legal_name"
                    type="text"
                    max_length={100}
                    label="Legal Name"
                    required={true}
                    errorMsg={formData.legal_name.error}
                    placeholder="Legal Name"
                    onChange={(value: string) => onChangeText("legal_name", value)}
                    value={formData.legal_name.value}
                />
                <TextInput
                    name="operating_name"
                    type="text"
                    max_length={100}
                    label="Operating Name"
                    required={true}
                    errorMsg={formData.operating_name.error}
                    placeholder="Operating Name"
                    onChange={(value: string) => onChangeText("operating_name", value)}
                    value={formData.operating_name.value}
                />
                <SelectDropdown
                    name="naics"
                    label="Industry Type"
                    required={true}
                    placeholder="-- select industry type --"
                    value={formData.naics.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("naics", option)}
                    errorMsg={formData.naics.error}
                    options={OPNAICS}
                />
                <SelectDropdown
                    name="type"
                    label="Organization Type"
                    required={true}
                    placeholder="-- select organization type --"
                    value={formData.type.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("type", option)}
                    errorMsg={formData.type.error}
                    options={OPEmployerTypes}
                />
                {/* <SelectDropdown
                    name="tech_company"
                    label="Is this a considered a tech company or does it have internal tech employees?"
                    required={true}
                    placeholder="-- select --"
                    value={formData.tech_company.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("tech_company", option)}
                    errorMsg={formData.tech_company.error}
                    options={OPBooleanOptions}
                /> */}
                <TextInput
                    name="non_canadian_emp"
                    type="text"
                    max_length={10}
                    pattern_mask="integer"
                    label="Approximately, how many non-Canadianss are currently employed?"
                    required={true}
                    errorMsg={formData.non_canadian_emp.error}
                    placeholder="Number of non-Canadian employees"
                    onChange={(value: string) => onChangeText("non_canadian_emp", value)}
                    value={formData.non_canadian_emp.value}
                />
                <SelectDropdown
                    name="lmia_in_past"
                    label="Has this CRA number ever been used to apply for an LMIA or LMIA-exempt application?"
                    required={true}
                    placeholder="-- select --"
                    value={formData.lmia_in_past.value}
                    onChange={(option: ISelectDropdownOption) => onChangeSelect("lmia_in_past", option)}
                    errorMsg={formData.lmia_in_past.error}
                    options={OPLMIAInPastOptions}
                />
            </div>
            <div className="immployer_admin__common_form__actions">
                {!formData.id &&
                    <button
                        className={`immployer_admin__btn immployer_admin__btn__secondary ${isProcessing ? "immployer_admin__btn__secondary__disabled" : ""}`}
                        onClick={() => isProcessing ? null : onDelete()}
                    >
                        {!formData.id ? 'Discard' : 'Delete'}
                    </button>
                }
                <button
                    className={`immployer_admin__btn immployer_admin__btn__primary ${isDisabled() ? "immployer_admin__btn__primary__disabled" : ""}`}
                    onClick={onSave}
                >
                    {
                        isProcessing
                            ? <i className='immployer_admin__btn_loader' />
                            : 'Save'
                    }
                </button>
            </div>
        </div>
    );
};

export default EmployerForm;